/**
 * Parser por plantilla para reportes XLTEK/Natus exportados del IMSS.
 * Los rótulos de cabecera son fijos en inglés aunque la narrativa sea en español.
 */

import { ExtractedTable, ExtractedTableCell } from '../types/extraction';
import { ageFromBirthDate, isValidPatientId } from '../utils/patientNameParser';

export interface XltekPatientHeader {
  name?: string;
  id?: string;
  age?: number;
  sex?: 'male' | 'female';
  studyDate?: string;
  physician?: string;
  referringPhysician?: string;
  technician?: string;
  handedness?: string;
  patientHistory?: string;
  confidence: number;
  source: 'table' | 'text';
}

const PATIENT_LABELS: Record<string, keyof XltekPatientHeader> = {
  patient: 'name',
  paciente: 'name',
  nombre: 'name',
  'nombre del paciente': 'name',
  id: 'id',
  expediente: 'id',
  nss: 'id',
  afiliacion: 'id',
  'no. exp': 'id',
  age: 'age',
  edad: 'age',
  gender: 'sex',
  sex: 'sex',
  sexo: 'sex',
  'study date': 'studyDate',
  'fecha de estudio': 'studyDate',
  physician: 'physician',
  'ref. phys.': 'referringPhysician',
  'ref phys': 'referringPhysician',
  'referring physician': 'referringPhysician',
  technician: 'technician',
  'técnico': 'technician',
  tecnico: 'technician',
  handedness: 'handedness',
  'patient history': 'patientHistory',
  'historia del paciente': 'patientHistory'
};

const XLTEK_TABLE_TITLES = [
  'motor side-to-side comparison table',
  'sensory side-to-side comparison table',
  'motor nerve conduction',
  'sensory nerve conduction',
  'needle emg examination',
  'myography',
  'f-wave',
  'h-reflex',
  'blink reflex',
  'repetitive nerve stimulation'
];

function normalizeLabel(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

function parseSex(value: string): 'male' | 'female' | undefined {
  const v = value.toLowerCase();
  if (/^(male|m|masculino|hombre)$/.test(v)) return 'male';
  if (/^(female|f|femenino|mujer)$/.test(v)) return 'female';
  return undefined;
}

function parseAge(value: string): number | undefined {
  const match = value.match(/(\d{1,3})\s*(y|a|años|anos|years?)?/i);
  return match ? parseInt(match[1], 10) : undefined;
}

function assignHeaderField(
  header: XltekPatientHeader,
  field: keyof XltekPatientHeader,
  rawValue: string
): void {
  const value = rawValue.trim();
  if (!value) return;

  if (field === 'sex') {
    const sex = parseSex(value);
    if (sex) header.sex = sex;
  } else if (field === 'age') {
    const age = parseAge(value);
    if (age !== undefined) header.age = age;
  } else if (field === 'name') {
    if (!header.name || value.length > header.name.length) header.name = value;
  } else if (field === 'patientHistory') {
    header.patientHistory = value;
  } else if (field === 'id') {
    if (isValidPatientId(value)) {
      header.id = value;
    }
  } else if (field === 'studyDate') {
    header.studyDate = value;
  } else if (field === 'physician') {
    header.physician = value;
  } else if (field === 'referringPhysician') {
    header.referringPhysician = value;
  } else if (field === 'technician') {
    header.technician = value;
  } else if (field === 'handedness') {
    header.handedness = value;
  }
}

/**
 * Extrae cabecera de paciente desde tablas con formato label | value.
 */
export function extractPatientFromTables(tables: ExtractedTable[]): XltekPatientHeader {
  const header: XltekPatientHeader = { confidence: 0, source: 'table' };
  let matchedFields = 0;

  for (const table of tables) {
    for (const row of table.rows) {
      if (row.length < 2) continue;

      const label = normalizeLabel(row[0].text);
      const fieldKey = PATIENT_LABELS[label];
      if (!fieldKey) continue;

      const valueCell = row.find((cell, idx) => idx > 0 && cell.text.trim().length > 0);
      if (!valueCell) continue;

      assignHeaderField(header, fieldKey, valueCell.text);
      matchedFields++;
    }
  }

  header.confidence = Math.min(0.95, 0.3 + matchedFields * 0.12);
  return header;
}

/**
 * Extrae cabecera desde texto plano con patrones XLTEK.
 */
export function extractPatientFromText(plainText: string): XltekPatientHeader {
  const header: XltekPatientHeader = { confidence: 0, source: 'text' };
  let matchedFields = 0;

  const patterns: Array<{ regex: RegExp; field: keyof XltekPatientHeader }> = [
    { regex: /\bPatient\s+([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s,.-]+?)(?=\s{2,}|\n|ID\b|Age\b|$)/i, field: 'name' },
    { regex: /\bPaciente\s*[:.]?\s*([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s,.-]+?)(?=\s{2,}|\n|ID\b|Edad\b|Expediente\b|$)/i, field: 'name' },
    { regex: /\bNombre(?:\s+del\s+paciente)?\s*[:.]?\s*([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s,.-]+?)(?=\s{2,}|\n|Edad\b|Sexo\b|$)/i, field: 'name' },
    { regex: /\b(?:ID|Expediente|NSS|No\.?\s*Exp\.?|Afiliaci[oó]n)\s*[:#]?\s*([A-Z0-9][A-Z0-9\-\/\.]{3,24})\b/i, field: 'id' },
    { regex: /\bAge\s+(\d{1,3}\s*y?)/i, field: 'age' },
    { regex: /\bEdad\s*[:.]?\s*(\d{1,3})\s*(?:a(?:ñ|n)os?|years?|y)?/i, field: 'age' },
    { regex: /\b(?:Gender|Sexo|Sex)\s*[:.]?\s*(Male|Female|M|F|Masculino|Femenino|Hombre|Mujer)\b/i, field: 'sex' },
    { regex: /\bStudy Date\s+([^\n\t]+)/i, field: 'studyDate' },
    { regex: /\bPatient History\s*([\s\S]+?)(?=\n\s*(?:Motor|Sensory|Needle|Myography|F-wave|H-reflex)|$)/i, field: 'patientHistory' },
    { regex: /\b(?:Historia(?:\s+cl[ií]nica)?(?:\s+del\s+paciente)?|Antecedentes)\s*[:.]?\s*([\s\S]+?)(?=\n\s*(?:Motor|Sensory|Needle|Neuroconducci|Electromiograf|Estudio)|$)/i, field: 'patientHistory' }
  ];

  for (const { regex, field } of patterns) {
    const match = plainText.match(regex);
    if (match?.[1]) {
      assignHeaderField(header, field, match[1]);
      matchedFields++;
    }
  }

  if (!header.age) {
    const dobMatch = plainText.match(
      /\b(?:Fecha\s+de\s+nacimiento|F\.?\s*Nac\.?|Fecha\s+Nac\.?|Fecha\s+de\s+Nac\.?)\s*[:.]?\s*(\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}|\d{4}[-/]\d{1,2}[-/]\d{1,2})/i
    );
    if (dobMatch?.[1]) {
      const derivedAge = ageFromBirthDate(dobMatch[1]);
      if (derivedAge !== undefined) {
        header.age = derivedAge;
        matchedFields++;
      }
    }
  }

  header.confidence = Math.min(0.85, 0.25 + matchedFields * 0.1);
  return header;
}

/**
 * Fusiona cabeceras de tabla y texto, priorizando tablas.
 */
export function mergePatientHeaders(
  fromTables: XltekPatientHeader,
  fromText: XltekPatientHeader
): XltekPatientHeader {
  const merged: XltekPatientHeader = {
    ...fromText,
    ...fromTables,
    confidence: Math.max(fromTables.confidence, fromText.confidence),
    source: fromTables.confidence >= fromText.confidence ? 'table' : 'text'
  };

  // Prefer longer patient history from text (narrative is often outside table cells)
  if (fromText.patientHistory && (fromText.patientHistory.length > (fromTables.patientHistory?.length || 0))) {
    merged.patientHistory = fromText.patientHistory;
  }

  return merged;
}

/**
 * Detecta si el documento es un reporte XLTEK.
 */
export function isXltekReport(plainText: string): boolean {
  const normalized = plainText.toLowerCase();
  const xltekMarkers = [
    'xltek',
    'motor side-to-side',
    'sensory side-to-side',
    'gabinete de electrodiagnostico',
    'instituto mexicano del seguro social',
    ...XLTEK_TABLE_TITLES
  ];
  if (xltekMarkers.some(marker => normalized.includes(marker))) return true;

  // Cabecera típica XLTEK: Patient + Study Date + Gender/Age
  const hasPatient = /\bpatient\b/i.test(plainText);
  const hasStudyDate = /\bstudy date\b/i.test(plainText);
  const hasGenderOrAge = /\b(gender|age)\b/i.test(plainText);
  return hasPatient && hasStudyDate && hasGenderOrAge;
}

/**
 * Extrae nombre del paciente desde el nombre del archivo (formatos IMSS).
 */
export function extractPatientFromFilename(fileName: string): {
  name?: string;
  studyDate?: string;
  confidence: number;
} {
  const base = fileName.replace(/\.(rtf|docx?|pdf|txt)$/i, '').trim();
  let name: string | undefined;
  let studyDate: string | undefined;
  let confidence = 0;

  // Formato: 16.12.2025 SAUL MARTIN TUYUB CASTILLO
  const datePrefix = base.match(/^(\d{1,2}\.\d{1,2}\.\d{2,4})\s+(.+)$/);
  if (datePrefix) {
    studyDate = datePrefix[1];
    name = datePrefix[2].trim();
    confidence = 0.7;
    return { name, studyDate, confidence };
  }

  // Formato: SANCHEZ EK, BLANCA YANELI
  const commaFormat = base.match(/^([A-ZÁÉÍÓÚÑ\s]+),\s*([A-ZÁÉÍÓÚÑ\s]+)$/i);
  if (commaFormat) {
    name = `${commaFormat[2].trim()} ${commaFormat[1].trim()}`;
    confidence = 0.65;
    return { name, studyDate, confidence };
  }

  // Formato: ORTIZ RAMIREZ CLAUDIA EDITH (todo mayúsculas, sin fecha)
  if (/^[A-ZÁÉÍÓÚÑ\s]+$/.test(base) && base.split(/\s+/).length >= 2) {
    name = base;
    confidence = 0.55;
    return { name, studyDate, confidence };
  }

  return { confidence: 0 };
}

/**
 * Convierte tablas RTF parseadas a ExtractedTable[].
 */
export function rtfRowsToExtractedTables(rows3d: string[][][]): ExtractedTable[] {
  return rows3d.map((rows, tableIndex) => {
    const extractedRows: ExtractedTableCell[][] = rows.map(row =>
      row.map(text => ({ text: text.trim() }))
    );
    const markdown = tableToMarkdown(extractedRows);
    return {
      id: `rtf_table_${tableIndex + 1}`,
      rows: extractedRows,
      markdown,
      source: { tableIndex }
    };
  });
}

function tableToMarkdown(rows: ExtractedTableCell[][]): string {
  if (rows.length === 0) return '';
  const normalized = rows.map(row => row.map(cell => cell.text.replace(/\|/g, '\\|')));
  const colCount = Math.max(...normalized.map(r => r.length));
  const pad = (row: string[]) => [...row, ...Array(Math.max(0, colCount - row.length)).fill('')];
  const header = pad(normalized[0]);
  const separator = Array(colCount).fill('---');
  const body = normalized.slice(1).map(pad);
  return [header, separator, ...body].map(row => `| ${row.join(' | ')} |`).join('\n');
}
